import { Component } from '@theme/component';

/**
 * Metres per unit, used both to format a distance and to read the radius input, which
 * is expressed in whichever unit is currently selected.
 */
const METRES_PER_UNIT = { mi: 1609.344, km: 1000 };

/** Mean Earth radius in metres (IUGG). */
const EARTH_RADIUS_M = 6371008.8;

/**
 * One in-flight Google Maps load per API key. The API can only be booted once per page,
 * so a second locator section on the same page has to await the first one's script.
 *
 * @type {Map<string, Promise<typeof google.maps>>}
 */
const mapsLoaders = new Map();

/**
 * Loads the Google Maps JavaScript API.
 *
 * @param {string} apiKey
 * @returns {Promise<any>}
 */
function loadGoogleMaps(apiKey) {
  const existing = mapsLoaders.get(apiKey);
  if (existing) return existing;

  const promise = new Promise((resolve, reject) => {
    // @ts-ignore - the Maps API is loaded at runtime and has no bundled types here.
    if (window.google?.maps) {
      // @ts-ignore
      resolve(window.google.maps);
      return;
    }

    const callbackName = '__storeLocatorMapsReady';
    const script = document.createElement('script');

    // @ts-ignore - Maps calls this global back once it has finished booting.
    window[callbackName] = () => {
      // @ts-ignore
      resolve(window.google.maps);
      // @ts-ignore
      delete window[callbackName];
    };

    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&loading=async&callback=${callbackName}`;
    script.async = true;
    script.onerror = () => reject(new Error('Google Maps failed to load'));

    document.head.append(script);
  });

  mapsLoaders.set(apiKey, promise);
  return promise;
}

/**
 * Great-circle distance in metres.
 *
 * Straight-line rather than driving distance: driving distance would mean a billed
 * Distance Matrix request per branch per search, and the difference does not change
 * which branch is nearest.
 *
 * @param {{lat: number, lng: number}} a
 * @param {{lat: number, lng: number}} b
 * @returns {number}
 */
function distanceBetween(a, b) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);

  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

/**
 * @typedef {object} Entry
 * @property {HTMLElement} element - The `<li>` for this branch.
 * @property {HTMLElement | null} numberEl - The rank badge.
 * @property {HTMLElement | null} distanceEl - Where the distance is written.
 * @property {string} name
 * @property {string} address
 * @property {{lat: number, lng: number} | null} position
 * @property {any} marker - The Google marker, once the map exists.
 * @property {number | null} distance - Metres from the last searched origin.
 * @property {boolean} visible
 */

/**
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} query
 * @property {HTMLInputElement} name
 * @property {HTMLInputElement} radius
 * @property {HTMLElement} list
 * @property {HTMLElement} count
 * @property {HTMLElement} empty
 * @property {HTMLElement} error
 * @property {HTMLElement} map
 * @property {HTMLElement} detail
 * @property {HTMLElement} detailSlot
 * @property {HTMLScriptElement} [strings]
 * @property {HTMLElement[]} [locations]
 * @property {HTMLInputElement[]} [unitInputs]
 */

/**
 * A branch locator: a searchable list of locations tied to a Google map.
 *
 * Everything except the map and the distance column works without JavaScript or without
 * an API key, so a missing or rejected key degrades to a plain list of branches.
 *
 * @extends {Component<Refs>}
 */
class StoreLocator extends Component {
  requiredRefs = ['form', 'query', 'name', 'radius', 'list', 'count', 'empty', 'error', 'map', 'detail', 'detailSlot'];

  /** @type {Entry[]} */
  #entries = [];

  /** @type {{lat: number, lng: number} | null} */
  #origin = null;

  /**
   * Where distances are measured from before anyone searches.
   *
   * Without one the Distance column is empty until a postal code is typed, which is
   * not what the reference does — it shows the network ordered from the primary branch
   * straight away. The first block is that branch, so reordering blocks in the editor
   * changes it.
   *
   * @type {{lat: number, lng: number} | null}
   */
  #defaultOrigin = null;

  /** @type {any} */
  #map = null;

  /** @type {any} */
  #originMarker = null;

  /** @type {any} */
  #infoWindow = null;

  /** @type {any} */
  #geocoder = null;

  /** The branch shown on its own, if any. @type {Entry | null} */
  #open = null;

  /** @type {Record<string, string>} */
  #strings = {};

  connectedCallback() {
    super.connectedCallback();

    this.#strings = this.#readStrings();
    this.#entries = this.#readEntries();
    this.#defaultOrigin = this.#entries.find((entry) => entry.position)?.position ?? null;
    this.#origin = this.#defaultOrigin;

    this.#render();
    this.#initMap();

    // A linked or refreshed detail view opens itself; Back and Forward move between
    // the branch and the results rather than off the page.
    this.#readUrl();
    window.addEventListener('popstate', this.#readUrl);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    window.removeEventListener('popstate', this.#readUrl);
  }

  // ---- event handlers ---------------------------------------------------------------

  /**
   * Runs a search. Never navigates — the results are rendered in place.
   *
   * @param {SubmitEvent} event
   */
  search = async (event) => {
    event.preventDefault();

    // Searching is a results action, so it comes back out of a single branch.
    this.#closeDetail({ push: true });

    const query = this.refs.query.value.trim();

    this.#setError('');

    if (!query) {
      // Clearing the postal code drops back to measuring from the primary branch,
      // not to no distances at all — the column should never go blank.
      this.#origin = this.#defaultOrigin;
      this.#clearOriginMarker();
      this.#render();
      return;
    }

    const position = await this.#geocode(query);

    if (!position) {
      this.#setError(this.#strings.notFound ?? '');
      return;
    }

    this.#origin = position;
    this.#render();
  };

  /** Switches between miles and kilometres. */
  changeUnit = () => {
    this.#closeDetail({ push: true });
    this.#render();
  };

  /**
   * Opens the branch whose name was clicked on its own.
   *
   * @param {Event} event
   */
  focusLocation = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const card = target.closest('[data-location]');
    const entry = this.#entries.find((item) => item.element === card);
    if (!entry) return;

    this.#openDetail(entry, { push: true });
  };

  /** Returns to the results. */
  backToResults = () => {
    this.#closeDetail({ push: true });
  };

  // ---- the detail view ----------------------------------------------------------------

  /**
   * Moves a branch's card out of the list and into the detail slot.
   *
   * The element itself is moved rather than copied. Its address, phone, hours and
   * directions are already there, so a copy would duplicate the data and put two
   * elements on the page carrying the same ids — including the hours dialog's.
   *
   * @param {Entry} entry
   * @param {{push?: boolean}} [options]
   */
  #openDetail(entry, { push = false } = {}) {
    this.#open = entry;

    entry.element.hidden = false;
    this.refs.detailSlot.appendChild(entry.element);
    this.refs.detail.hidden = false;
    this.dataset.view = 'detail';

    if (push) this.#writeUrl(entry.element.dataset.locationId ?? '');

    // One pin, close in — the rest of the network is not the subject any more.
    if (this.#map && entry.position) {
      this.#syncMarkers([entry]);
      this.#map.panTo(entry.position);
      this.#map.setZoom(15);
    }

    this.refs.detail.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  /** @param {{push?: boolean}} [options] */
  #closeDetail({ push = false } = {}) {
    if (!this.#open) return;

    this.#open = null;
    this.refs.detail.hidden = true;
    delete this.dataset.view;

    if (push) this.#writeUrl('');

    // Puts the card back in rank order and restores every pin.
    this.#render();
  }

  /**
   * Keeps the open branch in the URL, so a detail view can be linked, survives a
   * refresh, and the browser's Back button leaves it rather than the page.
   *
   * @param {string} id
   */
  #writeUrl(id) {
    const url = new URL(window.location.href);

    if (id) url.searchParams.set('location', id);
    else url.searchParams.delete('location');

    history.pushState({ location: id }, '', url.toString());
  }

  /** Opens whatever `?location=` names, if anything. */
  #readUrl = () => {
    const id = new URLSearchParams(window.location.search).get('location');

    if (!id) return this.#closeDetail();

    const entry = this.#entries.find((item) => item.element.dataset.locationId === id);
    if (entry) this.#openDetail(entry);
  };

  // ---- reading the DOM ---------------------------------------------------------------

  /** @returns {Record<string, string>} */
  #readStrings() {
    const { strings } = this.refs;
    if (!strings?.textContent) return {};

    try {
      return JSON.parse(strings.textContent);
    } catch {
      return {};
    }
  }

  /** @returns {Entry[]} */
  #readEntries() {
    return (this.refs.locations ?? []).map((element) => {
      const lat = Number.parseFloat(element.dataset.latitude ?? '');
      const lng = Number.parseFloat(element.dataset.longitude ?? '');
      const hasPosition = Number.isFinite(lat) && Number.isFinite(lng);

      return {
        element,
        numberEl: element.querySelector('.location-card__number'),
        distanceEl: element.querySelector('.location-card__distance'),
        name: element.dataset.name ?? '',
        address: element.dataset.address ?? '',
        position: hasPosition ? { lat, lng } : null,
        marker: null,
        distance: null,
        visible: true,
      };
    });
  }

  /** @returns {'mi' | 'km'} */
  get #unit() {
    const checked = (this.refs.unitInputs ?? []).find((input) => input.checked);
    return checked?.value === 'km' ? 'km' : 'mi';
  }

  // ---- rendering ---------------------------------------------------------------------

  /**
   * Recomputes distances, applies both filters, reorders the list and syncs the map.
   *
   * One pass over everything rather than incremental updates: with a handful of
   * branches it is cheap, and it removes every chance of the list, the numbers and the
   * markers disagreeing about what is currently shown.
   */
  #render() {
    const unit = this.#unit;
    const nameFilter = this.refs.name.value.trim().toLowerCase();
    const radius = Number.parseFloat(this.refs.radius.value);
    const radiusMetres = Number.isFinite(radius) && radius > 0 ? radius * METRES_PER_UNIT[unit] : null;

    for (const entry of this.#entries) {
      entry.distance = this.#origin && entry.position ? distanceBetween(this.#origin, entry.position) : null;

      const matchesName =
        !nameFilter ||
        entry.name.toLowerCase().includes(nameFilter) ||
        entry.address.toLowerCase().includes(nameFilter);

      // A branch with no coordinates can never be measured, so a radius search must not
      // silently drop it — it stays in the list, just without a distance.
      const withinRadius = radiusMetres === null || entry.distance === null || entry.distance <= radiusMetres;

      entry.visible = matchesName && withinRadius;
    }

    const visible = this.#entries.filter((entry) => entry.visible);

    if (this.#origin) {
      visible.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
    }

    for (const entry of this.#entries) {
      // The open card lives in the detail slot, not the list — leave it there and
      // leave it visible, whatever the current filters say.
      if (entry === this.#open) continue;
      entry.element.hidden = !entry.visible;
    }

    visible.forEach((entry, index) => {
      if (entry.numberEl) entry.numberEl.textContent = String(index + 1);
      if (entry.distanceEl) entry.distanceEl.textContent = this.#formatDistance(entry.distance, unit);
      // Moving the node rather than rebuilding it keeps the hours dialog inside it
      // intact, along with any open state and focus.
      if (entry !== this.#open) this.refs.list.append(entry.element);
    });

    this.refs.count.textContent = this.#formatCount(visible.length);
    this.refs.empty.hidden = visible.length > 0;

    this.#syncMarkers(visible);
  }

  /**
   * @param {number | null} metres
   * @param {'mi' | 'km'} unit
   * @returns {string}
   */
  #formatDistance(metres, unit) {
    if (metres === null) return '';

    const value = metres / METRES_PER_UNIT[unit];
    // Matches how the distances read at a glance: precise when they are close,
    // rounded once they are far enough that the decimals are noise.
    const formatted = value < 100 ? value.toFixed(2) : value.toFixed(0);
    const label = unit === 'km' ? this.#strings.unitKm : this.#strings.unitMi;

    return (this.#strings.distance ?? '')
      .replace('[distance]', formatted)
      .replace('[unit]', label ?? unit);
  }

  /**
   * @param {number} count
   * @returns {string}
   */
  #formatCount(count) {
    if (count === 1) return this.#strings.resultsOne ?? '';
    return (this.#strings.resultsOther ?? '').replace('[count]', String(count));
  }

  /** @param {string} message */
  #setError(message) {
    this.refs.error.textContent = message;
    this.refs.error.hidden = !message;
  }

  // ---- the map ------------------------------------------------------------------------

  async #initMap() {
    const apiKey = this.dataset.apiKey;
    if (!apiKey) return;

    let maps;

    try {
      maps = await loadGoogleMaps(apiKey);
    } catch {
      // The section already renders a fallback message when no key is set; a key that
      // is present but rejected leaves the list working, which is the important part.
      return;
    }

    const zoom = Number.parseInt(this.dataset.mapZoom ?? '', 10);

    this.#map = new maps.Map(this.refs.map, {
      zoom: Number.isFinite(zoom) ? zoom : 6,
      center: this.#entries.find((entry) => entry.position)?.position ?? { lat: 31.9686, lng: -99.9018 },
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
    });

    this.#geocoder = new maps.Geocoder();
    this.#infoWindow = new maps.InfoWindow();

    for (const entry of this.#entries) {
      if (!entry.position) continue;

      entry.marker = new maps.Marker({
        position: entry.position,
        title: entry.name,
        label: { text: ' ', color: '#ffffff', fontSize: '12px', fontWeight: '600' },
      });

      entry.marker.addListener('click', () => {
        this.#openInfoWindow(entry);
        entry.element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }

    // The markers were built after the first render, so place them now.
    this.#syncMarkers(this.#entries.filter((entry) => entry.visible));
  }

  /**
   * Puts exactly the currently visible branches on the map, numbered to match the list,
   * and frames them.
   *
   * @param {Entry[]} visible
   */
  #syncMarkers(visible) {
    if (!this.#map) return;

    // @ts-ignore - resolved at runtime.
    const maps = window.google.maps;
    const bounds = new maps.LatLngBounds();
    let bounded = 0;

    for (const entry of this.#entries) {
      if (!entry.marker) continue;
      entry.marker.setMap(entry.visible ? this.#map : null);
    }

    visible.forEach((entry, index) => {
      if (!entry.marker || !entry.position) return;

      entry.marker.setLabel({
        text: String(index + 1),
        color: '#ffffff',
        fontSize: '12px',
        fontWeight: '600',
      });

      bounds.extend(entry.position);
      bounded++;
    });

    if (this.#origin) {
      bounds.extend(this.#origin);
      bounded++;
    }

    if (bounded === 0) return;

    if (bounded === 1) {
      this.#map.setCenter(bounds.getCenter());
      return;
    }

    this.#map.fitBounds(bounds, 48);
  }

  /** @param {Entry} entry */
  #openInfoWindow(entry) {
    if (!this.#infoWindow || !entry.marker) return;

    // textContent, not innerHTML: the name and address are merchant settings and are
    // never treated as markup.
    const content = document.createElement('div');
    const name = document.createElement('strong');
    const address = document.createElement('div');

    name.textContent = entry.name;
    address.textContent = entry.address;
    content.append(name, address);

    this.#infoWindow.setContent(content);
    this.#infoWindow.open({ map: this.#map, anchor: entry.marker });
  }

  /**
   * Turns whatever the shopper typed into a point.
   *
   * @param {string} query
   * @returns {Promise<{lat: number, lng: number} | null>}
   */
  async #geocode(query) {
    if (!this.#geocoder) return null;

    try {
      const { results } = await this.#geocoder.geocode({ address: query });
      const location = results?.[0]?.geometry?.location;

      if (!location) return null;

      const position = { lat: location.lat(), lng: location.lng() };
      this.#showOriginMarker(position);

      return position;
    } catch {
      return null;
    }
  }

  /** @param {{lat: number, lng: number}} position */
  #showOriginMarker(position) {
    if (!this.#map) return;

    // @ts-ignore - resolved at runtime.
    const maps = window.google.maps;

    this.#clearOriginMarker();

    this.#originMarker = new maps.Marker({
      position,
      map: this.#map,
      zIndex: 1000,
      icon: {
        path: maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: '#0d3f6d',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      },
    });
  }

  #clearOriginMarker() {
    this.#originMarker?.setMap(null);
    this.#originMarker = null;
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}
